import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import * as unifiedApi from '../../services/unifiedApi';
import { RootState } from '../index';

// Thunk to fetch water profiles from the server (using unified API)
export const fetchWaterProfiles = createAsyncThunk(
  'waterQuality/fetchWaterProfiles',
  async (_, { rejectWithValue }) => {
    try {
      return await unifiedApi.getWaterProfiles();
    } catch (error) {
      console.error('Failed to fetch water profiles:', error);
      return rejectWithValue('Failed to fetch water profiles');
    }
  }
);

// Thunk to fetch water quality profiles (using unified API)
export const fetchWaterQuality = createAsyncThunk(
  'waterQuality/fetchWaterQuality',
  async (_, { rejectWithValue }) => {
    try {
      return await unifiedApi.getWaterQuality();
    } catch (error) {
      console.error('Failed to fetch water quality:', error);
      return rejectWithValue('Failed to fetch water quality');
    }
  }
);

// Thunk to analyze water quality (using unified API)
export const analyzeWaterQuality = createAsyncThunk<any, WaterQualityData>(
  'waterQuality/analyzeWaterQuality',
  async (waterQualityData, { rejectWithValue }) => {
    try {
      return await unifiedApi.analyzeWaterQuality(waterQualityData);
    } catch (error) {
      console.error('Failed to analyze water quality:', error);
      return rejectWithValue('Failed to analyze water quality');
    }
  }
);

// Function to create WaterQualityData from API response
const createWaterQualityData = (profile: any): WaterQualityData => {
  return {
    id: profile.id, // Preserve the ID from API response
    name: profile.name,
    ph: profile.ph,
    ec: profile.ec,
    tds: profile.ec * 500, // Approximate TDS based on EC
    temperature: 20, // Default temperature
    ca: profile.elements?.find((e: any) => e.elementSymbol === 'Ca')?.concentration || 0,
    mg: profile.elements?.find((e: any) => e.elementSymbol === 'Mg')?.concentration || 0,
    k: profile.elements?.find((e: any) => e.elementSymbol === 'K')?.concentration || 0,
    na: profile.elements?.find((e: any) => e.elementSymbol === 'Na')?.concentration || 0,
    cl: profile.elements?.find((e: any) => e.elementSymbol === 'Cl')?.concentration || 0,
    so4: profile.elements?.find((e: any) => e.elementSymbol === 'S')?.concentration || 0,
    no3: profile.elements?.find((e: any) => e.elementSymbol === 'N')?.concentration || 0,
    hco3: 0, // Not typically in API response
    co3: 0 // Not typically in API response
  };
};

// Type for individual elements that make up water quality
export interface WaterQualityElement {
  id: number;
  name: string;
  symbol: string;
  value: number;
  unit: string;
}

export interface WaterQualityData {
  id?: string; // Add id field for API compatibility
  name: string;
  ph: number;
  ec: number;
  tds: number;
  temperature: number;
  ca: number;
  mg: number;
  k: number;
  na: number;
  cl: number;
  so4: number;
  no3: number;
  hco3: number;
  co3: number;
  alkalinity?: number;
  hardness?: number;
}

interface WaterQualityState {
  elements: WaterQualityElement[];
  data: WaterQualityData | null;
  profiles: WaterQualityData[];
  loading: boolean;
  error: string | null;
  selectedWaterProfile: number | null;
}

const initialState: WaterQualityState = {
  elements: [],
  data: null,
  profiles: [],
  loading: false,
  error: null,
  selectedWaterProfile: null,
};

const waterQualitySlice = createSlice({
  name: 'waterQuality',
  initialState,
  reducers: {
    fetchWaterQualityStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchWaterQualitySuccess(state, action: PayloadAction<WaterQualityElement[]>) {
      state.elements = action.payload;
      state.loading = false;
      state.error = null;
    },
    fetchWaterQualityFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    updateWaterQualityElement(state, action: PayloadAction<WaterQualityElement>) {
      const index = state.elements.findIndex(element => element.id === action.payload.id);
      if (index !== -1) {
        state.elements[index] = action.payload;
      }
    },    updateWaterQualityData(state, action: PayloadAction<WaterQualityData>) {
      state.data = action.payload;
      
      // Check if this profile already exists by name, update it if it does,
      // otherwise add it to the profiles array
      const profileIndex = state.profiles.findIndex(p => p.name === action.payload.name);
      if (profileIndex >= 0) {
        state.profiles[profileIndex] = action.payload;
      } else if (action.payload.name) {
        // Only add to profiles if it has a name
        state.profiles.push(action.payload);
      }
    },
    addWaterProfile(state, action: PayloadAction<WaterQualityData>) {
      // Check for duplicates before adding
      const exists = state.profiles.some(profile => profile.name === action.payload.name);
      if (!exists) {
        state.profiles.push(action.payload);
      }
    },
    updateWaterProfile(state, action: PayloadAction<{ index: number, data: WaterQualityData }>) {
      const { index, data } = action.payload;
      if (index >= 0 && index < state.profiles.length) {
        state.profiles[index] = data;
      }
    },    setSelectedWaterProfile(state, action: PayloadAction<number | null>) {
      state.selectedWaterProfile = action.payload;
      if (action.payload !== null && action.payload >= 0 && action.payload < state.profiles.length) {
        state.data = state.profiles[action.payload];
      } else if (action.payload === null) {
        // Clear the selected data when deselecting
        state.data = null;
      }
    },deleteWaterProfile(state, action: PayloadAction<{ name: string } | { index: number }>) {
      if ('name' in action.payload) {
        // Delete by name - handle as properly narrowed type
        const { name } = action.payload;
        state.profiles = state.profiles.filter(profile => profile.name !== name);
      } else {
        // Delete by index - type is narrowed to { index: number }
        const { index } = action.payload;
        if (index >= 0 && index < state.profiles.length) {
          state.profiles.splice(index, 1);
        }
      }
    },  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWaterProfiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWaterProfiles.fulfilled, (state, action) => {
        state.loading = false;
        // Convert API response to WaterQualityData format and save to profiles
        state.profiles = action.payload.map((profile: any) => createWaterQualityData(profile));
        // If we don't have any data selected yet, select the first profile
        if (!state.data && state.profiles.length > 0) {
          state.data = state.profiles[0];
          state.selectedWaterProfile = 0;
        }
      })
      .addCase(fetchWaterProfiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  fetchWaterQualityStart,
  fetchWaterQualitySuccess,
  fetchWaterQualityFailed,
  updateWaterQualityElement,
  updateWaterQualityData,
  addWaterProfile,
  updateWaterProfile,
  setSelectedWaterProfile,
  deleteWaterProfile,
} = waterQualitySlice.actions;

// Selectors
export const selectWaterQualityData = (state: RootState) => state.waterQuality.data;
export const selectWaterQualityProfiles = (state: RootState) => state.waterQuality.profiles;
export const selectWaterQualityLoading = (state: RootState) => state.waterQuality.loading;
export const selectWaterQualityError = (state: RootState) => state.waterQuality.error;
export const selectSelectedWaterProfile = (state: RootState) => state.waterQuality.selectedWaterProfile;

export default waterQualitySlice.reducer;
